import React, { useEffect } from 'react';
import { Col, Form, Row } from 'antd';
import Box from 'components/Box';
import PercentDecimalInputPrecision from 'components/PercentDecimalInputPrecision';
import settingServiceActions from 'features/settingService/services/actions';
import shopSelectors from 'services/shop/selectors';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import SwitchBtn from '../../ServicesSettingV2/SwitchBtn';
import InputDefaultIncentive from './InputDefaultIncentive';
import InputRangeIncentive from './InputRangeIncentive';
import SettingTypeCheckBox from './SettingTypeCheckBox';
import { IShopDiscountParams } from 'features/settingService/services/types/paymentSetting';
type IDiscountPercentageProps = {};

type FormType = {
  discountStaff: number;
  discountOwner: number;
  discountStaffItem: number;
  discountOwnerItem: number;
  enableCashIncentive: boolean;
  discountType: 'PERCENT' | 'MONEY';
  discountValue: number;
  settingType: 'DEFAULT' | 'RANGE';
  discountTypeIncentive: 'PERCENT' | 'MONEY';
  rangeIncentives: { amount: number[]; discountValue: number }[];
};

const formInitialValues: FormType = {
  discountStaff: 0,
  discountOwner: 0,
  discountStaffItem: 0,
  discountOwnerItem: 0,
  enableCashIncentive: false,
  discountType: 'PERCENT',
  discountValue: 0,
  settingType: 'DEFAULT',
  discountTypeIncentive: 'PERCENT',
  rangeIncentives: [
    {
      amount: [0, 0],
      discountValue: 0,
    },
  ],
};

const DiscountPercentage: React.FC<IDiscountPercentageProps> = () => {
  const dispatch = useAppDispatch();
  const discountStore = shopSelectors.data.discountSetting();

  const [form] = Form.useForm<FormType>();

  const onFinish = (values: FormType) => {
    const body: IShopDiscountParams = {
      id: discountStore?.id,
      discountOwner: values.discountOwner,
      discountStaff: values.discountStaff,
      discountOwnerItem: values.discountOwnerItem,
      discountStaffItem: values.discountStaffItem,
      enableCashIncentive: values.enableCashIncentive,
      discountType: values.discountType,
      discountValue: values.discountValue,
      settingType: values.settingType,
      rangeIncentives:
        values.settingType === 'RANGE'
          ? values.rangeIncentives.map((item) => ({
            discountValue: item.discountValue,
            discountType: values.discountTypeIncentive,
            min: item.amount[0],
            max: item.amount[1],
          }))
          : [],
    };
    dispatch(settingServiceActions.postDiscount.fetch(body));
  };

  const handleStaffChange = (val: number | null, targetName: string) => {
    form.setFieldsValue({
      [targetName]: 100 - (val || 0),
    });
  };

  useEffect(() => {
    if (discountStore) {
      const initialRangeIncentives = [
        { amount: [0, 0], discountValue: 0, discountType: 'PERCENT' },
      ];
      const dataSetForm = {
        discountStaff: discountStore?.discountStaff,
        discountOwner: discountStore?.discountOwner,
        discountStaffItem: discountStore?.discountStaffItem,
        discountOwnerItem: discountStore?.discountOwnerItem,
        enableCashIncentive: discountStore?.enableCashIncentive,
        settingType: discountStore?.settingType || 'DEFAULT',
        discountType: discountStore?.discountType || 'PERCENT',
        discountValue: discountStore?.discountValue,
        discountTypeIncentive: discountStore?.rangeIncentives?.[0]?.discountType || 'PERCENT',
        rangeIncentives:
          discountStore?.rangeIncentives?.length > 0
            ? discountStore?.rangeIncentives?.map((item) => ({
              amount: [item.min || 0, item.max || 0],
              discountValue: item.discountValue || 0,
              discountType: item.discountType || 'PERCENT',
            }))
            : initialRangeIncentives,
      };

      form.setFieldsValue(dataSetForm);
    }
  }, [discountStore]);

  return (
    <Form<FormType>
      form={form}
      layout='vertical'
      onFinish={onFinish}
      initialValues={formInitialValues}
      style={{ height: '100%', position: 'relative' }}
    >
      <DiscountPercentageStyled>
        <BoxDiscountPercent>
          <Box mb='2' pt='4' display='flex' alignItems='center' style={{ gap: '16px' }}>
            <DiscountBox>
              <HeaderTitle>Discount Item</HeaderTitle>
              <Box display='flex' alignItems='center' style={{ gap: '16px' }}>
                <Form.Item name='discountStaffItem' label='Staff' style={{ flex: 1 }}>
                  <PercentDecimalInputPrecision
                    maxValue={100}
                    onChange={(val) => handleStaffChange(val, 'discountOwnerItem')}
                  />
                </Form.Item>
                <Form.Item name='discountOwnerItem' label='Company' style={{ flex: 1 }}>
                  <PercentDecimalInputPrecision
                    maxValue={100}
                    onChange={(val) => handleStaffChange(val, 'discountStaffItem')}
                  />
                </Form.Item>
              </Box>
            </DiscountBox>
            <DiscountBox>
              <HeaderTitle>Discount Ticket</HeaderTitle>
              <Box display='flex' alignItems='center' style={{ gap: '16px' }}>
                <Form.Item name='discountStaff' label='Staff' style={{ flex: 1 }}>
                  <PercentDecimalInputPrecision
                    maxValue={100}
                    onChange={(val) => handleStaffChange(val, 'discountOwner')}
                  />
                </Form.Item>
                <Form.Item name='discountOwner' label='Company' style={{ flex: 1 }}>
                  <PercentDecimalInputPrecision
                    maxValue={100}
                    onChange={(val) => handleStaffChange(val, 'discountStaff')}
                  />
                </Form.Item>
              </Box>
            </DiscountBox>
          </Box>
          <CashIncentiveSwitchContainer>
            <CashIncentiveLabel>Cash Incentive</CashIncentiveLabel>
            <Form.Item noStyle name={'enableCashIncentive'}>
              <SwitchBtn />
            </Form.Item>
          </CashIncentiveSwitchContainer>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const settingType = getFieldValue('settingType');
              const isDefaultDisabled = settingType !== 'DEFAULT';
              return (
                <FlexContainer>
                  <FlexItem>
                    <IncentiveStyled
                      onClick={() => form.setFieldsValue({ settingType: 'DEFAULT' })}
                    >
                      <div className='header'>
                        <div className='title'>Default</div>
                        <Form.Item name='settingType'>
                          <SettingTypeCheckBox baseKey='DEFAULT' />
                        </Form.Item>
                      </div>
                      <DisabledContainer className={isDefaultDisabled ? 'disabled' : ''}>
                        <InputDefaultIncentive />
                      </DisabledContainer>
                    </IncentiveStyled>
                  </FlexItem>
                  <FlexItem>
                    <IncentiveStyled onClick={() => form.setFieldsValue({ settingType: 'RANGE' })}>
                      <div className='header'>
                        <div className='title'>Range</div>
                        <Form.Item name='settingType'>
                          <SettingTypeCheckBox baseKey='RANGE' />
                        </Form.Item>
                      </div>
                      <DisabledContainer className={!isDefaultDisabled ? 'disabled' : ''}>
                        <InputRangeIncentive />
                      </DisabledContainer>
                    </IncentiveStyled>
                  </FlexItem>
                </FlexContainer>
              );
            }}
          </Form.Item>
        </BoxDiscountPercent>
        <StickyFooter>
          <ButtonFooter className='btn-reset' type='reset'>
            Reset
          </ButtonFooter>
          <ButtonFooter className='btn-save' type='submit'>
            Save
          </ButtonFooter>
        </StickyFooter>
      </DiscountPercentageStyled>
    </Form>
  );
};

export default DiscountPercentage;
const DiscountBox = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #fff;
  padding: 16px;
  .ant-form-item-label label {
    color: #1d2129;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
`;
const BoxDiscountPercent = styled.div`
  padding: 0 24px;
  flex: 1;
  overflow-y: auto;
  .ant-form-item {
    margin-bottom: 0;
  }
  .ant-col.ant-form-item-label {
    height: 32px;
    display: flex;
    align-items: center;
  }
  .label-cash-incentive {
    display: flex;
    align-items: center;
  }
  .ant-input {
    text-align: right;
    font-size: 20px !important;
  }
`;

const DiscountPercentageStyled = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 16px;
  height: 100%;
  .ant-input-number-input-wrap {
    height: 100%;
    input {
      height: 100%;
    }
  }

  .ant-input-affix-wrapper {
    .ant-input,
    .ant-input-suffix {
      color: #1d2129;
      font-family: Poppins;
      font-size: 24px !important;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }
  .ant-input.CurrencyInputPrecision {
    color: #1d2129;
    font-family: Poppins;
    font-size: 24px !important;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
  .ant-select .ant-select-selector .ant-select-selection-item {
    color: #1d2129;
    font-family: Poppins;
    font-size: 24px !important;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

const StickyFooter = styled.div`
  z-index: 1;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  align-items: center;
  background: #f5f5f5;
  box-shadow: 0 -4px 8px 0 rgba(0, 0, 0, 0.15);
  padding: 16px;
`;

const ButtonFooter = styled.button`
  display: flex;
  width: 208px;
  padding: 16px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: #e5e6eb;
  box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.4), 0px 4px 8px -3px rgba(45, 35, 66, 0.3),
    0px -3px 0px 0px #d6d6e7 inset;
  color: #1d2129;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;

  &.btn-save {
    background: #ff8890;
    box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.4), 0px 4px 8px -3px rgba(45, 35, 66, 0.3),
      0px -4px 0px 0px #e7646d inset;
    color: #fff;
  }
`;

const IncentiveStyled = styled.div`
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid #ccd4dc;
  background: #fff;
  .header {
    padding: 8px 16px;
    border-radius: 2px 2px 0 0;
    border-bottom: 1px solid #ccd4dc;
    background: #e5e6eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .title {
      color: #1d2129;
      font-family: Poppins;
      font-size: 20px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }
  .content {
    display: flex;
    padding: 16px;
    flex-direction: column;
    gap: 16px;
    align-self: stretch;
  }
`;

const DisabledContainer = styled.div`
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const HeaderTitle = styled.div`
  color: #1d2129;
  font-family: Poppins;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const CashIncentiveSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CashIncentiveLabel = styled.div`
  color: #1d2129;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const FlexItem = styled.div`
  flex: 1;
`;
